import { Box, Portal } from '@mantine/core';
import { useEffect, useState } from 'react';
import { ConfirmationPopup, ConfirmationPopupVariant } from './ConfirmationPopup';

const DEFAULT_AUTO_DISMISS_MS = 1500;

type ConfirmationState = {
  label: string;
  variant: ConfirmationPopupVariant;
};

// Shows the ConfirmationPopup centred over the viewport and auto-dismisses it.
// Returns an imperative `showConfirmation` to call after a successful action and
// the element to render somewhere in the component tree.
export function useConfirmationPopup(autoDismissMs = DEFAULT_AUTO_DISMISS_MS) {
  const [confirmation, setConfirmation] = useState<ConfirmationState | null>(null);

  useEffect(() => {
    if (!confirmation) return;
    const timeout = setTimeout(() => setConfirmation(null), autoDismissMs);
    return () => clearTimeout(timeout);
  }, [confirmation, autoDismissMs]);

  const showConfirmation = (label: string, variant: ConfirmationPopupVariant = 'success') => {
    setConfirmation({ label, variant });
  };

  const confirmationElement = confirmation ? (
    <Portal>
      <Box
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          pointerEvents: 'none',
        }}
      >
        <ConfirmationPopup label={confirmation.label} variant={confirmation.variant} />
      </Box>
    </Portal>
  ) : null;

  return { showConfirmation, confirmationElement };
}
